/* eslint-disable */
import * as React from 'react';
import { useState, useContext, FunctionComponent } from 'react';
import { useTranslation } from 'react-i18next';

// MUI
import Toolbar from '@mui/material/Toolbar';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Alert from '@mui/material/Alert';
import RadioGroup from '@mui/material/RadioGroup';
import FormControlLabel from '@mui/material/FormControlLabel';
import Radio from '@mui/material/Radio';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';

// Custom Components
import DynamicDialog from '@/ui/Dialog/DynamicDialog';
import DialogToolbarHeading from '@/ui/Dialog/DialogToolbarHeading';
import DialogToolbarButtonClose from '@/ui/Dialog/ToolbarButtons/DialogToolbarButtonClose';
import ToolbarFillContent from '@/ui/Toolbar/ToolbarFillContent';
import DialogActionButton from '@/ui/Dialog/DialogActionButton';
import DialogActionButtonCancel from '@/ui/Dialog/ActionButtons/DialogActionButtonCancel';
import DialogContext, { IDialogProps } from '@/context/DialogContext/DialogContext';
import SnackbarContext from '@/context/SnackbarContext/SnackbarContext';
import useApi from '@/lib/api/useApi';

type ImportSource = 'eplanovi' | 'ekatalog';

type ImportResult = {
  inserted: number;
  updated?: number; // eKatalog import is insert-only, it never reports an "updated" count
  skipped: number;
  total: number;
};

const SOURCE_CONFIG: Record<ImportSource, { accept: string; endpoint: string }> = {
  eplanovi: { accept: '.csv', endpoint: 'registar-prostornih-planova/import' },
  ekatalog: { accept: '.xlsx', endpoint: 'registar-prostornih-planova/import-ekatalog' },
};

const readFileAsText = (file: File): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = () => reject(reader.error);
    reader.readAsText(file);
  });

const readFileAsBase64 = (file: File): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const dataUrl = reader.result as string;
      resolve(dataUrl.substring(dataUrl.indexOf(',') + 1));
    };
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(file);
  });

const ImportRegistarProstornihPlanovaDialog: FunctionComponent<IDialogProps> = (props) => {
  const { hideDialog } = useContext(DialogContext);
  const { showNotification } = useContext(SnackbarContext);
  const { t } = useTranslation();
  const api = useApi();

  const { onClose } = props;

  const [source, setSource] = useState<ImportSource>('eplanovi');
  const [file, setFile] = useState<File | null>(null);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<ImportResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  const close = (dataChanged: boolean) => {
    onClose({ dataChanged });
    hideDialog();
  };

  const handleClose = () => {
    close(!!result);
  };

  const handleSourceChange = (evt: React.ChangeEvent<HTMLInputElement>) => {
    setSource(evt.target.value as ImportSource);
    setFile(null);
    setResult(null);
    setError(null);
  };

  const handleFileChange = (evt: React.ChangeEvent<HTMLInputElement>) => {
    const selected = evt.target.files && evt.target.files[0];
    setFile(selected || null);
    setResult(null);
    setError(null);
  };

  const handleImport = () => {
    if (!file) return;

    setLoading(true);
    setError(null);

    const { endpoint } = SOURCE_CONFIG[source];
    const bodyPromise: Promise<object> =
      source === 'eplanovi'
        ? readFileAsText(file).then((csv_content) => ({ csv_content }))
        : readFileAsBase64(file).then((xlsx_base64) => ({ xlsx_base64 }));

    bodyPromise
      .then((body) => api.post(endpoint, body))
      .then((resp) => {
        if (resp.success) {
          setResult(resp.data as unknown as ImportResult);
          showNotification('messages.import_registar_prostornih_planova_success', 'success');
        } else {
          const detailMessage = resp.error?.detail?.message;
          const msg =
            (typeof detailMessage === 'string' && detailMessage) ||
            resp.error?.message ||
            'error.import_registar_prostornih_planova';
          setError(msg);
          showNotification(msg, 'error');
        }
      })
      .catch((resp) => {
        const detailMessage = resp?.detail?.message;
        const msg =
          (typeof detailMessage === 'string' && detailMessage) ||
          resp?.message ||
          'error.import_registar_prostornih_planova';
        setError(msg);
        showNotification(msg, 'error');
      })
      .finally(() => {
        setLoading(false);
      });
  };

  return (
    <DynamicDialog open maxWidth="sm" onClose={handleClose}>
      <DialogTitle>
        <Toolbar variant="dense" disableGutters>
          <DialogToolbarHeading>{t('admin:titles.import_registar_prostornih_planova')}</DialogToolbarHeading>
          <ToolbarFillContent />
          <DialogToolbarButtonClose onClick={handleClose} />
        </Toolbar>
      </DialogTitle>
      <DialogContent>
        <Box my={2}>
          <Typography variant="body2" sx={{ mb: 1 }}>
            {t('admin:registar_prostornih_planova.import_hint')}
          </Typography>

          <Alert severity="info" sx={{ mb: 2 }}>
            {t('admin:registar_prostornih_planova.import_order_hint')}
          </Alert>

          <RadioGroup row value={source} onChange={handleSourceChange} sx={{ mb: 2 }}>
            <FormControlLabel
              value="eplanovi"
              control={<Radio size="small" />}
              label={t('admin:registar_prostornih_planova.source_eplanovi') as string}
            />
            <FormControlLabel
              value="ekatalog"
              control={<Radio size="small" />}
              label={t('admin:registar_prostornih_planova.source_ekatalog') as string}
            />
          </RadioGroup>

          <Button component="label" variant="outlined" startIcon={<UploadFileIcon />}>
            {file ? file.name : t('buttons.choose_file')}
            <input type="file" accept={SOURCE_CONFIG[source].accept} hidden onChange={handleFileChange} />
          </Button>

          {result && (
            <Box sx={{ mt: 3, display: 'flex', alignItems: 'center', gap: 1 }}>
              <CheckCircleIcon color="success" fontSize="small" />
              <Typography variant="body2">
                {result.updated === undefined
                  ? t('admin:registar_prostornih_planova.import_result_insert_only', {
                      inserted: result.inserted,
                      skipped: result.skipped,
                      total: result.total,
                    })
                  : t('admin:registar_prostornih_planova.import_result', {
                      inserted: result.inserted,
                      updated: result.updated,
                      skipped: result.skipped,
                      total: result.total,
                    })}
              </Typography>
            </Box>
          )}

          {error && (
            <Typography variant="body2" color="error" sx={{ mt: 3 }}>
              {t(error)}
            </Typography>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <DialogActionButtonCancel variant="outlined" onClick={handleClose} />
        <DialogActionButton
          variant="contained"
          startIcon={<UploadFileIcon />}
          onClick={handleImport}
          disabled={!file || loading}
          ariaLabel="import"
          ariaControls="import"
        >
          {t('buttons.import')}
        </DialogActionButton>
      </DialogActions>
    </DynamicDialog>
  );
};

export default ImportRegistarProstornihPlanovaDialog;
